import { effectiveWorkspaceAccess, type WorkspaceAccessKey } from '@/shared/permissions/workspaceAccess';
import type { CurrentUser } from '@/shared/types/auth';
import type { WidgetConfig } from './infrastructure/contracts/widget.contract';
import type { DashboardUser, ViewLevel } from './dashboard-resolution';

const LEVEL_ORDER: Record<ViewLevel, number> = { own: 0, department: 1, company: 2 };

export interface WidgetVisibilitySubject {
  user: DashboardUser | null | undefined;
  level: ViewLevel;
}

/**
 * The dashboard's ONE visibility decision for everything registered after the
 * role-dashboard rebuild.
 *
 * Deliberately does NOT read `ROLE_PERMISSIONS` (the static role map
 * `useRole().can()` resolves): per-person grants are exactly what the Team &
 * Access screen writes, and `/api/auth/me` already resolves them into
 * `workspaceAccess` + `actionPermissions`. Gating on the static map would make
 * a grant invisible on the dashboard forever, which is the bug this replaces.
 */
export function widgetIsVisible(config: WidgetConfig, { user, level }: WidgetVisibilitySubject): boolean {
  if (!user) return false;
  if (config.level && LEVEL_ORDER[level] < LEVEL_ORDER[config.level]) return false;
  if (config.workspace && !effectiveWorkspaceAccess(user as CurrentUser | null).includes(config.workspace as WorkspaceAccessKey)) return false;
  if (config.actionKey) {
    const held = Array.isArray(user.actionPermissions) ? user.actionPermissions : [];
    if (!held.includes(config.actionKey)) return false;
  }
  if (config.supportedRoles && config.supportedRoles.length > 0) {
    if (!user.role || !config.supportedRoles.includes(user.role)) return false;
  }
  return true;
}

/** Widgets that must state their source; legacy families are grandfathered. */
export function widgetDeclaresSource(config: WidgetConfig): boolean {
  return !!config.dataSource;
}
